// Ant Design theme configuration for Citizens Information
// Uses CSS custom properties for full tokenization
import { ThemeConfig } from 'antd';

export const brandTheme: ThemeConfig = {
  token: {
    // Brand Colors - Using CSS custom properties
    colorPrimary: 'var(--brand-primary)',
    colorSuccess: 'var(--brand-success)',
    colorInfo: 'var(--brand-info)',
    colorWarning: 'var(--brand-warning)',
    colorError: 'var(--brand-error)',
    colorLink: 'var(--brand-70)',
    colorLinkHover: 'var(--brand-80)',
    
    // Text Colors
    colorText: 'var(--text-primary)',
    colorTextSecondary: 'var(--text-secondary)',
    colorTextTertiary: 'var(--text-tertiary)',
    colorTextQuaternary: 'var(--text-tertiary)',
    
    // Background Colors
    colorBgContainer: 'var(--bg-primary)',
    colorBgElevated: 'var(--bg-secondary)',
    colorBgLayout: 'var(--bg-tertiary)',
    colorBgSpotlight: 'var(--bg-secondary)',
    
    // Border Colors
    colorBorder: 'var(--border-primary)',
    colorBorderSecondary: 'var(--border-secondary)',
    
    // Typography - Using CSS custom properties
    fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    fontSize: 14,
    fontSizeHeading1: 32,
    fontSizeHeading2: 24,
    fontSizeHeading3: 20,
    fontSizeHeading4: 16,
    fontSizeHeading5: 14,
    fontSizeLG: 16,
    fontSizeSM: 12,
    fontSizeXL: 18,
    
    // Font Weights
    fontWeightStrong: 600,
    
    // Line Heights
    lineHeight: 1.5,
    lineHeightHeading1: 1.2,
    lineHeightHeading2: 1.3,
    lineHeightHeading3: 1.4,
    lineHeightLG: 1.6,
    lineHeightSM: 1.4,
    
    // Border Radius - Using CSS custom properties
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,
    borderRadiusXS: 4,
    
    // Spacing - Using CSS custom properties
    padding: 16,
    paddingLG: 24,
    paddingSM: 12,
    paddingXS: 8,
    paddingXXS: 4,
    margin: 16,
    marginLG: 24,
    marginSM: 12,
    marginXS: 8,
    marginXXS: 4,
    
    // Control Heights
    controlHeight: 36,
    controlHeightLG: 44,
    controlHeightSM: 28,
    controlHeightXS: 24,
    
    // Effects - Using CSS custom properties
    boxShadow: 'var(--shadow-base)',
    boxShadowSecondary: 'var(--shadow-md)',
    boxShadowTertiary: 'var(--shadow-lg)',
    
    // Motion
    motionDurationFast: '0.1s',
    motionDurationMid: '0.2s',
    motionDurationSlow: '0.3s',
    motionEaseInOut: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
    motionEaseOut: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
  },
  components: {
    Button: {
      borderRadius: 8,
      controlHeight: 36,
      fontSize: 14,
      fontWeight: 500,
      boxShadow: 'var(--shadow-button)',
      primaryShadow: 'var(--shadow-sm)',
      colorPrimary: 'var(--brand-primary)',
      colorPrimaryHover: 'var(--brand-70)',
      colorPrimaryActive: 'var(--brand-80)',
      colorText: 'var(--text-primary)',
      colorTextSecondary: 'var(--text-secondary)',
      colorTextTertiary: 'var(--text-tertiary)',
      colorBgContainer: 'var(--bg-primary)',
      colorBorder: 'var(--border-primary)',
      // Ghost button styling for Citizens Info
      defaultBg: 'transparent',
      defaultColor: 'var(--brand-primary)',
      defaultBorderColor: 'var(--brand-primary)',
      defaultHoverBg: 'var(--brand-primary)',
      defaultHoverColor: 'var(--neutral-0)',
      defaultHoverBorderColor: 'var(--brand-primary)',
    },
    Card: {
      borderRadiusLG: 12,
      boxShadowTertiary: 'var(--shadow-card)',
      headerBg: 'var(--bg-secondary)',
    },
    Table: {
      borderRadius: 12,
      headerBg: 'var(--bg-secondary)',
      headerColor: 'var(--text-primary)',
      rowHoverBg: 'var(--bg-tertiary)',
      footerBg: 'var(--bg-primary)',
      footerColor: 'var(--text-secondary)',
    },
    Modal: {
      borderRadiusLG: 12,
      boxShadow: 'var(--shadow-modal)',
      headerBg: 'var(--bg-primary)',
      contentBg: 'var(--bg-primary)',
    },
    Input: {
      borderRadius: 8,
      controlHeight: 36,
      activeBorderColor: 'var(--border-focus)',
      hoverBorderColor: 'var(--border-brand)',
    },
    Select: {
      borderRadius: 8,
      controlHeight: 36,
      optionSelectedBg: 'var(--bg-brand)',
      optionActiveBg: 'var(--bg-tertiary)',
    },
    DatePicker: {
      borderRadius: 8,
      controlHeight: 36,
    },
    Form: {
      labelColor: 'var(--text-primary)',
      labelRequiredMarkColor: 'var(--brand-error)',
    },
    Menu: {
      itemBg: 'transparent',
      itemSelectedBg: 'var(--bg-brand)',
      itemSelectedColor: 'var(--text-on-brand)',
      itemHoverBg: 'var(--bg-tertiary)',
    },
    Dropdown: {
      borderRadius: 8,
      boxShadow: 'var(--shadow-lg)',
    },
    Tooltip: {
      borderRadius: 6,
      boxShadow: 'var(--shadow-lg)',
      colorBgSpotlight: 'var(--neutral-90)',
      colorTextLightSolid: 'var(--neutral-0)',
    },
    Popover: {
      borderRadius: 8,
      boxShadow: 'var(--shadow-lg)',
      colorBgElevated: 'var(--bg-primary)',
      colorText: 'var(--text-primary)',
    },
    Drawer: {
      borderRadius: 12,
      boxShadow: 'var(--shadow-xl)',
    },
    Tabs: {
      cardBg: 'var(--bg-secondary)',
      itemColor: 'var(--text-secondary)',
      itemSelectedColor: 'var(--brand-primary)',
      itemHoverColor: 'var(--text-primary)',
    },
  },
};